'use client';

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { donationsApiClient } from '@/lib/donations/api-donations-client';
import { redirectToPayment } from '@/lib/donations/redirectToPayment';
import { showWarningToast } from '@/components/ui/sonner';
import { savePendingDonation } from '@/features/donations/utils/donation-pending-local-storage.utils';

interface DonatModalProps {
  isOpen: boolean;
  onClose: () => void;
  author?: {
    id?: number;
    name?: string;
    username?: string;
    avatar?: {
      url?: string;
      thumbnailURL?: string;
    } | null;
    donation?: {
      donationUserId?: string | null;
      hasCard: boolean;
    };
  } | null;
}

export const DonatModal = ({ isOpen, onClose, author }: DonatModalProps) => {
  const [amount, setAmount] = useState<string>('');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);

  const authorName = author?.name || 'Автор';
  const authorUsername = author?.username || 'author';
  const authorAvatar = author?.avatar?.thumbnailURL || author?.avatar?.url;

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString());
    setSelectedAmount(value);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Только цифры
    setAmount(value);
    setSelectedAmount(null);
  };

  const handleClose = () => {
    setAmount('');
    setSelectedAmount(null);
    onClose();
  };

  const handlePayment = async () => {
    if (!amount || parseInt(amount) <= 0) return;

    if (!author?.donation?.donationUserId) {
      console.error('handlePayment: donationuserId not found');
      showWarningToast('Ошибка: ID получателя не найден');
      return;
    }

    if (!author?.id) {
      console.error('handlePayment: author.id not found');
      showWarningToast('Ошибка: ID автора не найден');
      return;
    }

    setIsLoadingPayment(true);

    try {
      const paymentLink = await donationsApiClient.fetchReceiverPaymentLink(
        author.donation.donationUserId,
      );

      if (!paymentLink) {
        showWarningToast('Ошибка: Не удалось получить ссылку на оплату');
        return;
      }

      savePendingDonation({
        authorId: author.id,
        authorName: author.name || author.username || 'Автор',
      });

      redirectToPayment({
        action: paymentLink,
        amount: parseInt(amount),
      });
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      showWarningToast(`Ошибка при получении ссылки на оплату: ${errorMessage}`);
      console.error('handlePayment error:', err);
    } finally {
      setIsLoadingPayment(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setSelectedAmount(null);
      setIsLoadingPayment(false);
    }
  }, [isOpen]);

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') return '?';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-[500px] rounded-2xl p-6 bg-white flex flex-col gap-6"
      >
        <DialogHeader className="relative flex items-center justify-center space-y-0">
          <DialogTitle className="text-2xl font-extrabold text-[#1B1D22] uppercase text-center">
            ПОДДЕРЖАТЬ АВТОРА
          </DialogTitle>
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-0 top-0 bg-[#E6E6E6] rounded-[7.5px] p-[9px] hover:opacity-80 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <Avatar isSquare className="h-12 w-12 !rounded-[12px]">
            <AvatarImage src={authorAvatar || ''} alt={authorName} />
            <AvatarFallback
              isSquare
              className="bg-[#F2F3F7] text-[#1B1D22] text-sm font-medium !rounded-[12px]"
            >
              {getInitials(authorName)}
            </AvatarFallback>
          </Avatar>

          <div className="flex flex-col">
            <span className="text-base font-medium text-[#1B1D22]">{authorName}</span>
            <span className="text-sm text-[#898989]">@{authorUsername}</span>
          </div>
        </div>

        <div>
          <label className="text-base font-semibold font-inter text-[#0A0A0A] mb-3 block">
            Сумма, ₽
          </label>

          <Input
            type="number"
            placeholder="Введите сумму от 49 до 3000 рублей"
            value={amount}
            onChange={handleAmountChange}
            className="h-[52px] bg-[#F2F3F7] border-0 text-[#1B1D22] placeholder:text-[#898989] text-sm font-inter rounded-xl p-4!"
          />
        </div>

        <div className="flex gap-2">
          {[100, 200, 300].map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => handleQuickAmount(value)}
              className={`flex-1 h-8 max-w-[51px] rounded-[40px] font-medium text-sm transition-colors ${
                selectedAmount === value
                  ? 'bg-[#505CFF] text-white'
                  : 'bg-[#F2F3F7] text-[#1B1D22] hover:bg-[#E8E9ED]'
              }`}
            >
              {value}₽
            </button>
          ))}
        </div>

        <Button
          onClick={handlePayment}
          disabled={!amount || parseInt(amount) <= 0 || isLoadingPayment}
          className="w-full h-[46px] bg-[#505CFF] hover:bg-[#505CFF]/90 text-white text-sm font-normal rounded-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingPayment ? 'Переход к оплате...' : 'Перейти к оплате'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
